import React, { createContext, ReactNode, useContext } from 'react';
import api from '../services/api';

import { Offer } from '../models/Offer';
import {
  CreateOrderFromOfferAttributes,
  DeliveryMethod,
  Order,
} from '../models/Order';

export interface SendOfferRequestBody {
  inquiryId: number;
  offerDetails: string;
  validUntil: string;
  deliveryPrice: number;
  deliveryMethod: DeliveryMethod;
  products: Array<{
    id: number;
    price: number;
    quantity: number;
    removeProductFromOffer?: boolean;
  }>;
}

interface OfferContextType {
  sendOffer: (data: SendOfferRequestBody) => Promise<void>;
  getOffer: (offerId: string) => Promise<Offer>;
  getOffers: (
    limit: number,
    page: number,
    search?: string,
  ) => Promise<{
    offers: Offer[];
    totalOffers: number;
    totalPages: number;
  }>;
  createOrderFromOffer: (
    offerNumber: string,
    data: CreateOrderFromOfferAttributes,
  ) => Promise<Order>;
}

const OfferContext = createContext<OfferContextType | undefined>(undefined);

export const useOffer = () => {
  const context = useContext(OfferContext);
  if (!context) {
    throw new Error('useOffer must be used within an OfferProvider');
  }
  return context;
};

interface OfferProviderProps {
  children: ReactNode;
}

export const OfferProvider: React.FC<OfferProviderProps> = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const sendOffer = async (data: SendOfferRequestBody) => {
    try {
      await api.post(`${apiUrl}/offers`, data);
    } catch (error: any) {
      console.error('Failed to send offer', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getOffer = async (offerId: string): Promise<Offer> => {
    try {
      const response = await api.get(`${apiUrl}/offers/public/${offerId}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch offer', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  const getOffers = async (
    limit: number,
    page: number,
    search = '',
  ): Promise<{
    offers: Offer[];
    totalOffers: number;
    totalPages: number;
  }> => {
    try {
      const response = await api.get(`${apiUrl}/offers`, {
        params: {
          limit,
          page,
          search,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch offers', error);
      throw new Error(error.response?.data?.message || 'Server error');
    }
  };

  /**
   * Tworzy zamówienie na podstawie oferty
   * @param offerId ID oferty
   * @returns ID utworzonego zamówienia
   */
  const createOrderFromOffer = async (
    offerNumber: string,
    data: CreateOrderFromOfferAttributes,
  ): Promise<Order> => {
    try {
      const response = await api.put(
        `${apiUrl}/offers/${offerNumber}/order`,
        data,
      );

      if (!response.data.success || !response.data.order) {
        throw new Error('Nie udało się utworzyć zamówienia');
      }

      return response.data.order;
    } catch (error: any) {
      console.error('Failed to create order from offer', error);
      throw new Error(
        error.response?.data?.message ||
          'Nie udało się utworzyć zamówienia z oferty. Spróbuj ponownie później.',
      );
    }
  };

  return (
    <OfferContext.Provider
      value={{
        sendOffer,
        getOffer,
        getOffers,
        createOrderFromOffer,
      }}
    >
      {children}
    </OfferContext.Provider>
  );
};
